import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@/components/common/GoogleTextField";
import { toast } from "sonner";
import { getAppAuthSecret, verifyAndSetAppAuth } from "@/api/auth";
import { useAuthStore } from "@/stores/auth";

interface AppAuthSetupDialogProps {
  open: boolean;
  mode: "setup" | "edit";
  onClose: () => void;
}

export function AppAuthSetupDialog({
  open,
  mode,
  onClose,
}: AppAuthSetupDialogProps) {
  const email = useAuthStore((state) => state.user?.email ?? "");
  const [password, setPassword] = useState("");
  const [existingOtp, setExistingOtp] = useState("");
  const [otp, setOtp] = useState("");
  const [qr, setQr] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [loading, setLoading] = useState(false);
  const close = () => {
    setPassword("");
    setExistingOtp("");
    setOtp("");
    setQr("");
    setSetupToken("");
    onClose();
  };
  const submit = async () => {
    setLoading(true);
    try {
      if (!qr) {
        const result = await getAppAuthSecret(
          email,
          password,
          existingOtp || undefined,
        );
        if (!result.qrCode || !result.setupToken)
          throw new Error("Unable to start authenticator setup.");
        setQr(result.qrCode);
        setSetupToken(result.setupToken);
        setPassword("");
        setExistingOtp("");
      } else {
        const result = await verifyAndSetAppAuth({
          userName: email,
          otp,
          setupToken,
        });
        if (!result.setupCompleted)
          throw new Error("Unable to complete authenticator setup.");
        toast.success("Authenticator saved. Sign in again to continue.");
        close();
        useAuthStore.getState().forceLogout();
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Unable to update your authenticator.",
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : close}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {mode === "edit" ? "Replace authenticator" : "Set up authenticator"}
      </DialogTitle>
      <DialogContent>
        {!qr ? (
          <div className="space-y-4 pt-2">
            <p>
              Confirm your password to set up an authenticator. Enrollment is
              optional.
            </p>
            <TextField
              label="Current password"
              type="password"
              autoComplete="current-password"
              fullWidth
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={loading}
            />
            {mode === "edit" && (
              <TextField
                label="Code from current authenticator"
                slotProps={{
                  htmlInput: { maxLength: 6, inputMode: "numeric" },
                }}
                fullWidth
                value={existingOtp}
                onChange={(event) =>
                  setExistingOtp(event.target.value.replace(/\D/g, ""))
                }
                disabled={loading}
              />
            )}
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <p>
              Scan this QR code with your authenticator, then enter its six
              digit code.
            </p>
            <img
              src={`data:image/png;base64,${qr}`}
              alt="Authenticator setup QR code"
              className="mx-auto h-48 w-48"
            />
            <TextField
              label="New authenticator code"
              fullWidth
              autoComplete="one-time-code"
              slotProps={{ htmlInput: { maxLength: 6, inputMode: "numeric" } }}
              value={otp}
              onChange={(event) =>
                setOtp(event.target.value.replace(/\D/g, ""))
              }
              disabled={loading}
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={
            loading ||
            (qr
              ? otp.length !== 6
              : !password || (mode === "edit" && existingOtp.length !== 6))
          }
        >
          {loading ? "Please wait?" : qr ? "Save authenticator" : "Continue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
